import type { FC } from 'react';
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { useLocation } from 'react-router';
import { useSSRPreviewMode } from '../../bff-api/customers/useSSRPreviewMode';
import { LoadingIndicator } from '../../components/loading-indicator/LoadingIndicator';
import { Redirect } from '../../components/redirect/Redirect';
import { buildHttpError } from '../../utils/error-utils';
import { isServer } from '../../utils/utils';
import { BloomreachProductType } from '../search/hooks/use-search';
import { useSearch } from '../search/hooks/use-search/useSearch';
import { cleanPathname } from '../search/utils/search-url-params-utils';
import { configHtmlCache } from '../utils/config-html-cache';
import { ShowCategoryPage } from './ShowCategoryPage';
import { isCategoryInfo } from './api/getContentData';
import { useContentData } from './hooks/useContentData';

const Products: FC = () => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const serverContext = useServerContext();
    const location = useLocation();
    const categoryPath = cleanPathname(location.pathname);
    // Trick to make the search query not dependant on the contentInfo
    useSearch(BloomreachProductType.hardgood, categoryPath);

    const {
        data: contentInfo,
        isError,
        error,
    } = useContentData({
        categoryPath,
    });

    if (isError && error) {
        // Make sure we do not throw the error from the server side when in preview mode
        if (isSSRPreviewMode && isServer) {
            // eslint-disable-next-line no-console
            console.warn('Preview mode: suppressing error', error);
            return <LoadingIndicator isLoading />;
        }
        throw buildHttpError(error);
    }

    if (isServer && serverContext) {
        configHtmlCache(serverContext.res, 900); // 15 minutes
    }

    if (
        contentInfo?.response?.type === 'REDIRECT' &&
        contentInfo?.response?.urlMapping?.destinationUrl
    ) {
        return (
            <Redirect
                to={contentInfo?.response?.urlMapping?.destinationUrl}
                type="sg"
                status={contentInfo?.response?.urlMapping?.statusCode}
            />
        );
    }

    if (isCategoryInfo(contentInfo)) {
        return <ShowCategoryPage categoryInfo={contentInfo} />;
    }

    if (isSSRPreviewMode) {
        return <LoadingIndicator isLoading />;
    }

    throw new Error('Invalid content data!');
};

export default Products;
